// public/v3/src/components/overrides-modal.jsx
// Studio workflow overrides — surfaces fields that belong to monthly work:
//   1. Skip this month (with reason)
//   2. AI prompt overrides (freeform additions to the per-patient prompt)
//
// Persistence mirrors time-entry-edit-modal.jsx: PUT the full flat patient blob
// with `_updatedAt` stripped. Server-side audit fires automatically on the PUT.
//
// onSaved(updatedRaw) is called after a successful PUT so the caller can
// update local state and invalidate the draft cache.

import React, { useState } from 'react';
import { ccmApi } from '../lib/api';

const inputStyle = {
  width: '100%',
  padding: '8px 10px',
  border: '1px solid var(--line)',
  borderRadius: 'var(--r)',
  fontSize: 14,
  background: 'var(--surface)',
  color: 'var(--ink-1)',
};

export function OverridesModal({ patient, month, onClose, onSaved }) {
  const raw = patient?._raw || {};
  const existingSkip = (raw.skipMonth && month && raw.skipMonth[month]) || null;
  const initialSkipChecked = !!existingSkip;
  const initialSkipReason = existingSkip?.reason || '';
  const initialPromptOverrides = raw.aiPromptOverrides || '';

  const [skipChecked, setSkipChecked] = useState(initialSkipChecked);
  const [skipReason, setSkipReason] = useState(initialSkipReason);
  const [promptOverrides, setPromptOverrides] = useState(initialPromptOverrides);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  if (!patient) return null;

  function validate() {
    if (skipChecked && !skipReason.trim()) {
      return 'A reason is required when skipping the month.';
    }
    return null;
  }

  async function handleSave() {
    const err = validate();
    if (err) { setError(err); return; }
    setError(null);
    setBusy(true);
    try {
      if (!patient._raw) throw new Error('patient data not loaded');

      // Build the patch. Preserve _updatedAt on the body so the server's 409
      // optimistic-lock check fires on stale writes (server strips it before
      // storing — see server/routes/patients.js:144).
      const nextRaw = { ...patient._raw };

      // skipMonth: per-month keyed map. Set or delete the entry for `month`.
      const prevSkip = (patient._raw.skipMonth && typeof patient._raw.skipMonth === 'object') ? patient._raw.skipMonth : {};
      const nextSkip = { ...prevSkip };
      if (month) {
        if (skipChecked) {
          nextSkip[month] = { reason: skipReason.trim(), ts: new Date().toISOString() };
        } else {
          delete nextSkip[month];
        }
      }
      // Only attach skipMonth if there's at least one entry; keep blob clean.
      if (Object.keys(nextSkip).length) {
        nextRaw.skipMonth = nextSkip;
      } else if ('skipMonth' in nextRaw) {
        delete nextRaw.skipMonth;
      }

      // AI prompt overrides — string field. Save only if non-empty; clear
      // otherwise so blank textareas don't leave a junk empty-string in the
      // blob that downstream prompt-builders might still concatenate.
      const promptTrimmed = promptOverrides.trim();
      if (promptTrimmed) {
        nextRaw.aiPromptOverrides = promptTrimmed;
      } else if ('aiPromptOverrides' in nextRaw) {
        delete nextRaw.aiPromptOverrides;
      }

      const resp = await ccmApi.updatePatient(patient.id, nextRaw);
      const fresh = resp && resp.updatedAt ? resp.updatedAt : null;
      // Pass the fresh _updatedAt back so the parent's local _raw stays in sync
      // for subsequent optimistic-lock checks.
      onSaved?.(fresh ? { ...nextRaw, _updatedAt: fresh } : nextRaw);
      onClose?.();
    } catch (e) {
      if (e && e.status === 409) {
        setError('Patient was updated elsewhere. Refresh and try again.');
        // eslint-disable-next-line no-console
        console.error('OverridesModal save 409:', e);
      } else {
        const msg = e?.message || 'Save failed';
        setError(msg);
        // eslint-disable-next-line no-console
        console.error('OverridesModal save failed:', e);
        alert(`Could not save overrides: ${msg}`);
      }
    } finally {
      setBusy(false);
    }
  }

  const headerName = `${raw.fname || ''} ${raw.lname || ''}`.trim() || patient.name || patient.id;

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div
        className="modal"
        role="dialog"
        aria-modal="true"
        aria-label={`Overrides for ${headerName}`}
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: 520 }}>
        <div className="modal-head">
          <div>
            <h3>Overrides — {headerName}</h3>
            {month && <div className="sub">Skip-month applies to {month}</div>}
          </div>
          <button className="btn ghost icon" onClick={onClose} aria-label="Close" disabled={busy}>✕</button>
        </div>
        <div className="modal-body">
          {/* ── Skip this month ── */}
          <div className="field">
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, color: 'var(--ink-1)' }}>
              <input
                type="checkbox"
                checked={skipChecked}
                onChange={(e) => setSkipChecked(e.target.checked)}
                disabled={busy || !month}
              />
              Skip this month{month ? ` (${month})` : ''}
            </label>
            {skipChecked && (
              <div style={{ marginTop: 8 }}>
                <div className="field-label" style={{ marginBottom: 4 }}>Reason for skipping (required)</div>
                <textarea
                  value={skipReason}
                  onChange={(e) => setSkipReason(e.target.value)}
                  disabled={busy}
                  rows={2}
                  placeholder="Hospitalized, on vacation, lost to follow-up, etc."
                  style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit' }}
                />
              </div>
            )}
          </div>

          {/* ── AI prompt overrides ── */}
          <details style={{ marginTop: 4 }}>
            <summary style={{ cursor: 'pointer', fontSize: 13, color: 'var(--ink-2)', userSelect: 'none', padding: '4px 0' }}>
              AI prompt overrides (optional)
            </summary>
            <div style={{ marginTop: 8 }}>
              <textarea
                value={promptOverrides}
                onChange={(e) => setPromptOverrides(e.target.value)}
                disabled={busy}
                rows={4}
                placeholder="Patient-specific notes to inject into the AI prompt — e.g., 'prefers phone over portal', 'spouse manages meds', 'avoid mention of weight'."
                style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit' }}
              />
            </div>
          </details>

          {error && (
            <div style={{ color: 'var(--bad, #d33)', fontSize: 13, marginTop: 8 }}>{error}</div>
          )}
        </div>
        <div className="modal-foot">
          <span/>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn" onClick={onClose} disabled={busy}>Cancel</button>
            <button className="btn primary" onClick={handleSave} disabled={busy}>
              {busy ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
